<template>
  <ContentWrap>
    <el-form :inline="true" :model="q" class="-mb-15px">
      <el-form-item label="活动">
        <el-input v-model="q.keyword" clearable placeholder="活动标题" @keyup.enter="search" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="q.status" clearable class="!w-140px">
          <el-option label="报名中" :value="1" />
          <el-option label="进行中" :value="2" />
          <el-option label="已结束" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="search">搜索</el-button>
        <el-button v-hasPermi="['bosscircle:activity:create']" type="primary" @click="open()">新增</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      show-icon
      title="「主办资源」决定该活动出现在哪个企业/协会主页的「活动」Tab；不选主办资源的活动只出现在活动广场，不会挂到任何资源详情页。"
    />
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column prop="id" label="编号" width="80" />
      <el-table-column prop="activityTitle" label="活动" min-width="180" show-overflow-tooltip />
      <el-table-column prop="activityType" label="类型" width="80">
        <template #default="s">{{ s.row.activityType === 1 ? '线上' : '线下' }}</template>
      </el-table-column>
      <el-table-column prop="location" label="地点" min-width="140" show-overflow-tooltip />
      <el-table-column label="主办方" min-width="160" show-overflow-tooltip>
        <template #default="s">{{ s.row.organizerName || '未指定' }}</template>
      </el-table-column>
      <el-table-column label="人数" width="110">
        <template #default="s">{{ s.row.currentParticipants }} / {{ s.row.maxParticipants || '不限' }}</template>
      </el-table-column>
      <el-table-column prop="startTime" label="开始时间" :formatter="dateFormatter" width="180" />
      <el-table-column prop="status" label="状态" width="90" />
      <el-table-column label="操作" width="140" fixed="right">
        <template #default="s">
          <el-button v-hasPermi="['bosscircle:activity:update']" link @click="open(s.row.id)">编辑</el-button>
          <el-button v-hasPermi="['bosscircle:activity:delete']" link type="danger" @click="del(s.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination v-model:page="q.pageNo" v-model:limit="q.pageSize" :total="total" @pagination="load" />
  </ContentWrap>

  <Dialog v-model="visible" :title="form.id ? '编辑活动' : '新增活动'">
    <el-form ref="fref" :model="form" :rules="rules" label-width="90px">
      <el-form-item label="标题" prop="activityTitle">
        <el-input v-model="form.activityTitle" />
      </el-form-item>
      <el-form-item label="描述">
        <el-input v-model="form.activityDesc" type="textarea" />
      </el-form-item>
      <el-form-item label="封面">
        <UploadImg v-model="form.activityCover" />
      </el-form-item>
      <el-form-item label="主办资源">
        <el-select
          v-model="organizerPick"
          clearable
          filterable
          remote
          allow-create
          default-first-option
          reserve-keyword
          :remote-method="searchResources"
          :loading="resourceLoading"
          placeholder="输入名称匹配库里企业/协会；没有匹配时直接回车按新名称保存"
          style="width: 100%"
        >
          <el-option v-for="r in organizerOptions" :key="r.id" :label="r.name" :value="r.id" />
        </el-select>
        <div class="mt-4px text-12px text-#909399">{{ organizerHint }}</div>
      </el-form-item>
      <el-form-item label="类型">
        <el-radio-group v-model="form.activityType">
          <el-radio :value="1">线上</el-radio>
          <el-radio :value="2">线下</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="时间">
        <el-date-picker v-model="range" type="datetimerange" value-format="YYYY-MM-DDTHH:mm:ss" />
      </el-form-item>
      <el-form-item label="地点">
        <el-input v-model="form.location" />
      </el-form-item>
      <el-form-item label="人数">
        <el-input-number v-model="form.maxParticipants" :min="1" />
      </el-form-item>
      <el-form-item label="状态">
        <el-radio-group v-model="form.status">
          <el-radio :value="1">报名中</el-radio>
          <el-radio :value="2">进行中</el-radio>
          <el-radio :value="3">已结束</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button type="primary" @click="save">保存</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { dateFormatter } from '@/utils/formatTime'
import * as Api from '@/api/business/activity'
import * as ResourceApi from '@/api/business/resource'

defineOptions({ name: 'BossCircleActivity' })

const message = useMessage()
const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const visible = ref(false)
const fref = ref()
const range = ref<string[]>([])

const q = reactive({ pageNo: 1, pageSize: 10, keyword: '', status: undefined as number | undefined })
const form = reactive<any>({})
const rules = { activityTitle: [{ required: true, message: '请输入标题' }] }

const resourceLoading = ref(false)
const resourceOptions = ref<ResourceApi.ResourceVO[]>([])
/** 下拉的绑定值：数字串=库里的资源 ID，其它文字=allow-create 新增的主办名称 */
const organizerPick = ref<string>()

const searchResources = async (keyword: string) => {
  resourceLoading.value = true
  try {
    const data: any = await ResourceApi.getResourcePage({
      pageNo: 1,
      pageSize: 20,
      keyword: keyword || undefined
    })
    resourceOptions.value = data.list || []
  } finally {
    resourceLoading.value = false
  }
}

/** remote 模式下搜索结果会换掉整份 options，已选中的主办会被换掉后只显示裸 ID，故并回列表 */
const organizerOptions = computed(() => {
  const list = resourceOptions.value.map((r) => ({ id: String(r.id), name: r.name || String(r.id) }))
  const pick = organizerPick.value
  if (pick && /^\d+$/.test(pick) && !list.some((o) => o.id === pick)) {
    list.unshift({ id: pick, name: form.organizerName || '已选资源' })
  }
  return list
})

const organizerHint = computed(() => {
  const pick = organizerPick.value
  if (!pick) return '不选主办时，活动只出现在活动列表，不进任何资源详情的「活动」Tab。'
  return /^\d+$/.test(pick)
    ? '选中后活动会出现在该资源的「活动」Tab，主办名称以资源表真值覆盖手填串。'
    : '库里没有同名资源：该名称只作为主办文字挂在活动上，不会出现在任何资源详情页。'
})

const load = async () => {
  loading.value = true
  try {
    const d: any = await Api.getActivityPage(q)
    list.value = d.list || []
    total.value = d.total || 0
  } finally {
    loading.value = false
  }
}

const search = () => {
  q.pageNo = 1
  load()
}

const open = async (id?: number) => {
  Object.assign(form, {
    id: undefined,
    activityTitle: '',
    activityDesc: '',
    activityCover: '',
    activityType: 1,
    location: '',
    maxParticipants: 100,
    status: 1,
    organizerResourceId: undefined
  })
  range.value = []
  resourceOptions.value = []
  organizerPick.value = undefined
  if (id) {
    Object.assign(form, await Api.getActivity(id))
    range.value = [form.startTime, form.endTime]
    // 后端给的是数字 ID，el-option 的 value 用字符串；不统一会让下拉只显示裸 ID
    if (form.organizerResourceId) {
      organizerPick.value = String(form.organizerResourceId)
    } else {
      // 历史活动只有手填主办名：回填成 allow-create 的文本态，别让下拉显示空白
      organizerPick.value = form.organizerName || undefined
    }
  }
  visible.value = true
  // 原先 searchResources 收到空关键字直接 return，remote 下拉永远打开即空，故进弹窗先预载一页
  void searchResources('')
}

const save = async () => {
  if (!(await fref.value?.validate().catch(() => false)) || range.value.length !== 2) {
    message.warning('请填写完整时间')
    return
  }
  form.startTime = range.value[0]
  form.endTime = range.value[1]
  const pick = String(organizerPick.value || '').trim()
  if (/^\d+$/.test(pick)) {
    form.organizerResourceId = Number(pick)
    form.organizerName = organizerOptions.value.find((o) => o.id === pick)?.name || form.organizerName
  } else {
    // 新增的主办名不是资源 ID，塞进 organizerResourceId 会让后端按 ID 查不到而报「资源不存在」
    form.organizerResourceId = undefined
    form.organizerName = pick || undefined
  }
  if (form.id) await Api.updateActivity(form)
  else await Api.createActivity(form)
  message.success('保存成功')
  visible.value = false
  load()
}

const del = async (id: number) => {
  await message.delConfirm()
  await Api.deleteActivity(id)
  message.success('删除成功')
  load()
}

onMounted(load)
</script>
